import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import { i18next, dialogs } from '@yola/ws-sdk';
import dialogTypes from '../../dialogs/constants/dialog-types';
import generateSnippetId from '../helpers/generate-snippet-id';

const { HeadingGroup, ActionButton, Stack, TreeView } = designSystem;
const { useFieldArray } = reactHookForm;

const getCaptions = () => ({
  emptyState: i18next.t('No code snippets yet'),
  addButton: i18next.t('Add code'),
  edit: i18next.t('Edit'),
});

const CodeSnippetListField = ({
  title,
  control,
  fieldName,
  triggerId,
  dialogType,
  codeGroup,
  isAvailable,
  showSettingsDialog,
}) => {
  const captions = getCaptions();

  const { fields, replace } = useFieldArray({
    control,
    name: fieldName,
  });

  const onAddSnippet = useCallback(() => {
    dialogs.operations.show(dialogTypes.CODE_SNIPPET_DIALOG, {
      triggerId,
      dialogType,
      codeGroup,
      snippet: {
        isNew: true,
        name: `Snippet ${fields.length + 1}`,
        code: '',
        runtimeRendering: false,
      },
      onSubmit: (snippet) => {
        showSettingsDialog({ [fieldName]: [...fields, { ...snippet, id: generateSnippetId() }] });
      },
      onCancel: showSettingsDialog,
    });
  }, [codeGroup, triggerId, dialogType, showSettingsDialog, fields, fieldName]);

  const onEditSnippet = useCallback(
    (snippet, index) => {
      dialogs.operations.show(dialogTypes.CODE_SNIPPET_DIALOG, {
        triggerId,
        dialogType,
        codeGroup,
        snippet,
        onSubmit: (updatedSnippet) => {
          showSettingsDialog({ [fieldName]: fields.toSpliced(index, 1, updatedSnippet) });
        },
        onDelete: () => {
          showSettingsDialog({ [fieldName]: fields.toSpliced(index, 1) });
        },
        onCancel: showSettingsDialog,
      });
    },
    [codeGroup, triggerId, dialogType, showSettingsDialog, fields, fieldName]
  );

  const onOrderChange = useCallback(
    (snippets) => {
      const fieldsMap = new Map(fields.map((field) => [field.id, field]));
      const reorderedFields = snippets.map((item) => fieldsMap.get(item.id));

      replace(reorderedFields);
    },
    [replace, fields]
  );

  const items = fields.map((snippet, index) => ({
    id: snippet.id,
    title: snippet.name,
    order: index.toString(),
    iconGlyph: 'drag-icon',
    droppable: false,
    actions: [
      {
        label: captions.edit,
        glyph: 'pen',
        iconType: 'accent',
        onClick: () => onEditSnippet(snippet, index),
      },
    ],
  }));

  return (
    <Stack gap="spacing-xs">
      <HeadingGroup title={title} {...(!fields.length && { description: captions.emptyState })}>
        {!!fields.length && <TreeView items={items} onChange={onOrderChange} />}
      </HeadingGroup>
      <div>
        <ActionButton disabled={!isAvailable} label={captions.addButton} onClick={onAddSnippet} />
      </div>
    </Stack>
  );
};

CodeSnippetListField.propTypes = {
  title: PropTypes.string.isRequired,
  control: PropTypes.shape({}).isRequired,
  fieldName: PropTypes.string.isRequired,
  triggerId: PropTypes.string.isRequired,
  dialogType: PropTypes.string.isRequired,
  codeGroup: PropTypes.string.isRequired,
  isAvailable: PropTypes.bool.isRequired,
  showSettingsDialog: PropTypes.func.isRequired,
};

export default CodeSnippetListField;
